import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface TitresHistoryDTO {
  id?: string;
  typeInstrument: string;
  transactionDate?: string;
  region?: string;
  isin: string;
  amortizationMode?: string;
  residualDuration?: number;
  outstandingAmount?: number;
  ratioEchange?: number;
  coupon?: number;
  maturityDate?: string;
  quantity?: number;
  averagePrice?: number;
  spread?: number;
  averageYield?: number;
  numberOfTransactions?: number;
  couponRan?: number;
  nominalVolumeExchanged?: number;
  interestAccrued?: number;
  totalVolumeTraded?: number;
}

export interface PageResponse<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
  };
  totalElements: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class TitresHistoryService {
  private http = inject(HttpClient);
  private readonly apiUrl = 'income/v1/titres/history';

  /**
   * Get all history titres with pagination
   */
  getAllTitres(page: number = 0, size: number = 10): Observable<PageResponse<TitresHistoryDTO>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<PageResponse<TitresHistoryDTO>>(this.apiUrl, { params });
  }

  /**
   * Get titre history by ID
   */
  getTitreById(id: string): Observable<TitresHistoryDTO> {
    return this.http.get<TitresHistoryDTO>(`${this.apiUrl}/${id}`);
  }

  /**
   * Update an existing titre history
   */
  updateTitre(id: string, titre: TitresHistoryDTO): Observable<TitresHistoryDTO> {
    return this.http.put<TitresHistoryDTO>(`${this.apiUrl}/${id}`, titre);
  }

  /**
   * Delete a titre history
   */
  deleteTitre(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Import titres history from Excel file
   */
  importFromExcel(file: File): Observable<TitresHistoryDTO[]> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<TitresHistoryDTO[]>(`${this.apiUrl}/import/excel`, formData);
  }
}
